import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { useRepubContext } from '@cx-web/feature-repub';
import { pollForWindowProperty } from '@cx-web/utils/pollForWindowObject';

import { type Bet } from '~/components/BettingInsights/types';

export const useOCRepubBetslip = ({
  bet,
  onClick,
}: {
  bet: Bet;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { repubIs } = useRepubContext();
  const [isBetInBetslip, setIsBetInBetslip] = useState(false);

  useEffect(() => {
    if (!repubIs.OC) return;

    const handleBetAdded = (e: Event) => {
      const customEvent = e as CustomEvent<{ betId: number }>;
      if (customEvent.detail.betId === bet.betId) {
        setIsBetInBetslip(true);
      }
    };

    const handleBetRemoved = (e: Event) => {
      const customEvent = e as CustomEvent<{ betId: number }>;
      if (customEvent.detail.betId === bet.betId) {
        setIsBetInBetslip(false);
      }
    };

    const handleCleared = () => {
      setIsBetInBetslip(false);
    };

    window.addEventListener('oc-betslip:bet-added', handleBetAdded);
    window.addEventListener('oc-betslip:bet-removed', handleBetRemoved);
    window.addEventListener('oc-betslip:cleared', handleCleared);

    const initState = async () => {
      await pollForWindowProperty('ocBetslip');
      window.ocBetslip && setIsBetInBetslip(window.ocBetslip?.isInBetslip(bet.betId));
    };
    initState();

    return () => {
      window.removeEventListener('oc-betslip:bet-added', handleBetAdded);
      window.removeEventListener('oc-betslip:bet-removed', handleBetRemoved);
      window.removeEventListener('oc-betslip:cleared', handleCleared);
    };
  }, [repubIs, bet.betId]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (window.ocBetslip?.isInBetslip(bet.betId)) {
        window.ocBetslip.removeBet({ betId: bet.betId });
      } else {
        window.ocBetslip?.addBet(bet);
        window.ocBetslip?.toggle();
      }

      onClick?.(e);
    },
    [onClick, bet],
  );

  return {
    isBetInBetslip,
    handleClick,
  };
};
